import { Send, CheckCircle, XCircle, BookOpen, Clock } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import type { Campaign, CampaignStatus, DispatchStats } from '@/types/api.types';

interface Props {
  campaign: Campaign;
  stats: DispatchStats;
}

const STATUS_LABEL: Record<CampaignStatus, string> = {
  draft: 'Rascunho',
  scheduled: 'Agendada',
  running: 'Em andamento',
  completed: 'Concluída',
  failed: 'Falhou',
  cancelled: 'Cancelada',
};

const STATUS_VARIANT: Record<CampaignStatus, 'default' | 'secondary' | 'destructive' | 'outline'> = {
  draft: 'secondary',
  scheduled: 'outline',
  running: 'default',
  completed: 'secondary',
  failed: 'destructive',
  cancelled: 'destructive',
};

export function CampaignStatsHeader({ campaign, stats }: Props) {
  const progress = stats.total > 0 ? Math.round(((stats.delivered + stats.failed) / stats.total) * 100) : 0;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <h1 className="text-2xl font-semibold">{campaign.name}</h1>
        <Badge variant={STATUS_VARIANT[campaign.status]}>{STATUS_LABEL[campaign.status]}</Badge>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
        {[
          { icon: Send, label: 'Total', value: stats.total, color: 'text-primary' },
          { icon: Clock, label: 'Pendentes', value: stats.pending, color: 'text-yellow-600' },
          { icon: Send, label: 'Enviados', value: stats.sent, color: 'text-blue-600' },
          { icon: CheckCircle, label: 'Entregues', value: stats.delivered, color: 'text-green-600' },
          { icon: XCircle, label: 'Falhas', value: stats.failed, color: 'text-destructive' },
        ].map(({ icon: Icon, label, value, color }) => (
          <Card key={label}>
            <CardContent className="flex items-center gap-3 pt-4">
              <Icon className={`h-6 w-6 ${color}`} />
              <div>
                <p className="text-lg font-bold">{value}</p>
                <p className="text-xs text-muted-foreground">{label}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {stats.total > 0 && (
        <div className="space-y-1">
          <div className="flex justify-between text-xs text-muted-foreground">
            <span>Progresso</span>
            <span>{progress}%</span>
          </div>
          <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
            <div
              className="h-full bg-primary transition-all"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      )}
    </div>
  );
}
